import { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, Grid, IconButton,
  LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { FormProvider, RHFFileField, RHFTextField } from '../../common/hook-form'
import { apiConstants, getGeneric, postMultipart } from '../../common/constants'
import DownloadSampleFile from '../../components/DownloadSampleFile'

// Plain-English tooltip — no jargon, action-oriented.
const CSV_FORMAT_HELP = (
  <Box sx={{ p: 0.5 }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.75 }}>How this works</Typography>
    <Typography sx={{ fontSize: 11.5, mb: 1, lineHeight: 1.5 }}>
      Couriers (Delhivery, BlueDart, etc.) send weekly weight reports as a CSV.
      You upload that report here so we can compare what they actually weighed
      against what was declared.
    </Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Steps</Typography>
    <Box component='ol' sx={{ pl: 2.5, m: 0, mb: 1, '& li': { fontSize: 11.5, mb: 0.4, lineHeight: 1.4 } }}>
      <li>Click <strong>Download Sample CSV</strong> for the template.</li>
      <li>Paste in the weights you have — leave blanks where you don't.</li>
      <li>Drop the file in the box and hit <strong>Upload</strong>.</li>
    </Box>
    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Good to know</Typography>
    <Box component='ul' sx={{ pl: 2.5, m: 0, '& li': { fontSize: 11.5, mb: 0.4, lineHeight: 1.4 } }}>
      <li>Only the AWB number is required. Weight columns can be left blank.</li>
      <li>Weights are in <strong>kilograms</strong>.</li>
      <li>If anything's wrong with a row, you'll get an error report you can fix and re-upload.</li>
    </Box>
  </Box>
)

// The /status endpoint returns a FLAT shape per the API doc:
//   { uploadId, status, totalRows, processedRows, updatedCount,
//     skippedCount, errorCount, errorReportUrl }
// The history endpoint returns a NESTED bulk-action shape with
//   fileProgress + metaDataObject. We tolerate both so this component
//   works regardless of which shape it receives — flat takes priority.
const STATUS_CFG = {
  PENDING: { color: '#1e40af', bg: '#dbeafe', label: 'Pending' },
  PROCESSING: { color: '#a16207', bg: '#fef3c7', label: 'Processing' },
  IN_PROGRESS: { color: '#a16207', bg: '#fef3c7', label: 'Processing' },
  COMPLETED: { color: '#15803d', bg: '#dcfce7', label: 'Completed' },
  PARTIAL_SUCCESS: { color: '#c2410c', bg: '#ffedd5', label: 'Partial Success' },
  FAILED: { color: '#b91c1c', bg: '#fee2e2', label: 'Failed' },
  STUCK: { color: '#6b21a8', bg: '#f3e8ff', label: 'Stuck — needs intervention' },
}

const TERMINAL = ['COMPLETED', 'PARTIAL_SUCCESS', 'FAILED', 'STUCK']

function deriveStatus(record) {
  // Flat-shape fields take priority; fall back to nested bulk-action shape.
  const raw = record?.status || record?.fileProgress
  const errorCount = record?.errorCount ?? record?.metaDataObject?.errorCount ?? 0
  const execFailed = record?.executionStatus?.failed || 0
  // BE may return COMPLETED even with errors — surface as PARTIAL_SUCCESS.
  if (raw === 'COMPLETED' && (errorCount > 0 || execFailed > 0)) return 'PARTIAL_SUCCESS'
  return raw || 'PENDING'
}

const pickUploadId = (record) =>
  record?.uploadId || record?.uuid || record?.id || null

export default function MisUpload() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadId, setUploadId] = useState(null)
  const [record, setRecord] = useState(null) // full bulk-action record from BE
  const pollRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()

  const methods = useForm({ defaultValues: { file: null, misFileTag: '' } })
  const { handleSubmit, reset } = methods

  // Poll status every 4s until terminal — guard against unmount + stale uploads.
  useEffect(() => {
    if (!uploadId) return undefined
    let cancelled = false
    const tick = () => {
      getGeneric(`${apiConstants.WEIGHT_LEDGER_MIS_STATUS}/${uploadId}/status`)
        .then((res) => {
          if (cancelled) return
          const r = res?.data || null
          setRecord(r)
          if (TERMINAL.includes(deriveStatus(r))) {
            clearInterval(pollRef.current)
          }
        })
        .catch((err) => console.error('poll error', err))
    }
    tick()
    pollRef.current = setInterval(tick, 4000)
    return () => {
      cancelled = true
      clearInterval(pollRef.current)
    }
  }, [uploadId])

  const onSubmit = async (data) => {
    if (!data.file?.[0]) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' })
      return
    }
    setIsSubmitting(true)
    setRecord(null)
    setUploadId(null)
    const formData = new FormData()
    formData.append('file', data.file[0])
    // BE uses `remark` for the human-readable note (matches the bulk-action
    // shape); fall back to misFileTag for callers who hard-coded the doc name.
    if (data.misFileTag) {
      formData.append('remark', data.misFileTag)
      formData.append('misFileTag', data.misFileTag)
    }
    try {
      const res = await postMultipart(apiConstants.WEIGHT_LEDGER_MIS_UPLOAD, formData)
      const id = pickUploadId(res?.data)
      if (id) {
        setUploadId(id)
        setRecord(res?.data || null)
        enqueueSnackbar('Upload accepted — processing started', { variant: 'success' })
        reset()
      } else {
        enqueueSnackbar(res?.data?.errorMsg || 'Upload failed', { variant: 'error' })
      }
    } catch (err) {
      console.error(err)
      enqueueSnackbar(err?.response?.data?.errorMsg || 'Upload failed', { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Read flat-shape fields first; fall back to nested bulk-action shape.
  const meta = record?.metaDataObject || {}
  const totalRows = record?.totalRows ?? meta.totalValidRows
  const processedRows = record?.processedRows ?? meta.processedRows
  const updatedCount = record?.updatedCount ?? meta.updatedCount
  const skippedCount = record?.skippedCount ?? meta.skippedCount
  const errorCount = record?.errorCount ?? meta.errorCount
  const statusKey = record ? deriveStatus(record) : null
  const cfg = statusKey ? STATUS_CFG[statusKey] : null
  const pct = totalRows ? Math.min(100, Math.round(((processedRows ?? 0) / totalRows) * 100)) : 0
  // Flat shape doesn't carry the original file link; nested shape does.
  const originalFileLink = record?.s3RefToFile?.signedLink

  return (
    <Stack spacing={2}>
      <Card>
        <CardHeader
          title={
            <Stack direction='row' spacing={0.75} alignItems='center'>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>MIS Upload</Typography>
              <Tooltip
                title={CSV_FORMAT_HELP}
                arrow
                placement='right'
                componentsProps={{
                  tooltip: { sx: { maxWidth: 460, p: 1.5, fontSize: 11 } },
                }}
              >
                <IconButton size='small' sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
          subheader='Upload courier MIS CSV — processed asynchronously, status auto-refreshes below.'
          subheaderTypographyProps={{ fontSize: 12 }}
          action={<DownloadSampleFile fileName='WEIGHT_MIS_UPLOAD' label='Download Sample CSV' />}
        />
        {/* Feedback item A3: document the expected update cadence for merchant MIS uploads. */}
        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f8fafc', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>Update frequency</Typography>
          <Box component='ul' sx={{ pl: 2.25, m: 0, '& li': { fontSize: 11.5, color: '#64748b', lineHeight: 1.5 } }}>
            <li>Merchant billed weight — <strong>MONTHLY</strong></li>
            <li>Merchant billed weight slab — <strong>MONTHLY</strong></li>
          </Box>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <RHFFileField name='file' fileType='.csv' />
              </Grid>
              <Grid item xs={12} md={4}>
                <RHFTextField name='misFileTag' label='File Tag (optional)' placeholder='e.g. Delhivery_May_W1' />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton loading={isSubmitting} variant='contained' type='submit'>
                  Upload
                </LoadingButton>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {/* Live status panel */}
      {uploadId && (
        <Card>
          <CardContent>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  Upload {uploadId.slice(0, 8)}…
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  Auto-refreshing every 4s until processing completes.
                </Typography>
              </Box>
              {cfg && (
                <Chip
                  size='small'
                  label={cfg.label}
                  sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: cfg.bg, color: cfg.color }}
                />
              )}
            </Stack>

            {totalRows != null && (
              <>
                <LinearProgress
                  variant='determinate'
                  value={pct}
                  sx={{ height: 8, borderRadius: 1, mb: 1 }}
                />
                <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 1.5 }}>
                  {processedRows ?? 0} of {totalRows} rows processed ({pct}%)
                </Typography>
              </>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Total Rows</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{totalRows ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Updated</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#15803d' }}>
                  {updatedCount ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Skipped</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#a16207' }}>
                  {skippedCount ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Errors</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#b91c1c' }}>
                  {errorCount ?? '—'}
                </Typography>
              </Grid>
            </Grid>

            {originalFileLink && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(originalFileLink, '_blank')}
                >
                  Download Original CSV
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  )
}
